import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

export type AbortSignal = 'abort' | null;

/**
 * Some event handlers cause normal debounces to fire right away, most likely due to some
 * render. This debounce uses a ref to avoid that.
 *
 * @param func - The function to debounce.
 * @param delay - The debounce delay in milliseconds.
 * @param abortSignal - A ref to a value that will cause the debounce to abort if set as 'abort'. It will be cleared once cancelled.
 * @param deps - Dependencies for the callback function.
 */
export function useDebounce(
  func: (...args: any[]) => void,
  delay: number,
  abortSignal?: MutableRefObject<AbortSignal>,
  deps: any[] = [],
  onScheduled?: () => void,
  onReleased?: () => void
) {
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastArgsRef = useRef<any[] | null>(null);

  // Memoize the function to ensure a stable reference
  const memoizedFunc = useCallback(func, deps);

  // Refs so the unmount cleanup can call the latest function with the latest hooks
  const memoizedFuncRef = useRef(memoizedFunc);
  memoizedFuncRef.current = memoizedFunc;
  const onReleasedRef = useRef(onReleased);
  onReleasedRef.current = onReleased;

  const debounce = useCallback(
    (...args: any[]) => {
      lastArgsRef.current = args;
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      onScheduled?.();

      debounceTimeout.current = setTimeout(() => {
        debounceTimeout.current = null;
        lastArgsRef.current = null;
        onReleased?.();
        memoizedFunc(...args);
      }, delay);
    },
    [memoizedFunc, delay, onReleased, onScheduled]
  );

  // Flush on unmount so a pending debounce fires with the latest args instead of dropping the value (TAS-3436: text fields silently lost input when users typed then clicked away inside the debounce window).
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
        debounceTimeout.current = null;
        onReleasedRef.current?.();
        if (lastArgsRef.current) {
          const args = lastArgsRef.current;
          lastArgsRef.current = null;
          memoizedFuncRef.current(...args);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (abortSignal?.current === 'abort' && debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      onReleased?.();
      debounceTimeout.current = null;
      lastArgsRef.current = null;
      if (abortSignal) {
        abortSignal.current = null;
      }
    }
  }, [abortSignal?.current, abortSignal, onReleased]);

  return debounce;
}

/**
 * A debounce hook that maintains separate timers per key.
 * Useful for scenarios where multiple fields need independent debouncing,
 * such as custom fields where typing in one field shouldn't cancel the debounce of another.
 *
 * @param func - The function to call with (key, value) after debounce.
 * @param delay - The debounce delay in milliseconds.
 * @param deps - Dependencies for the callback function.
 * @param onScheduled - Called when a debounce timer starts for a key.
 * @param onReleased - Called when a debounce timer completes or is cancelled for a key.
 */
export function useDebounceByKey<T>(
  func: (key: string, value: T) => void,
  delay: number,
  deps: any[] = [],
  onScheduled?: (key: string) => void,
  onReleased?: (key: string) => void
): (key: string, value: T) => void {
  const debounceTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const activeKeys = useRef<Set<string>>(new Set());
  const lastValues = useRef<Map<string, T>>(new Map());

  // Use refs to avoid stale closures in setTimeout callbacks
  const onScheduledRef = useRef(onScheduled);
  const onReleasedRef = useRef(onReleased);
  onScheduledRef.current = onScheduled;
  onReleasedRef.current = onReleased;

  const memoizedFunc = useCallback(func, deps);
  const memoizedFuncRef = useRef(memoizedFunc);
  memoizedFuncRef.current = memoizedFunc;

  const debounce = useCallback(
    (key: string, value: T) => {
      lastValues.current.set(key, value);
      const existingTimeout = debounceTimeouts.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      if (!activeKeys.current.has(key)) {
        activeKeys.current.add(key);
        onScheduledRef.current?.(key);
      }

      const timeout = setTimeout(() => {
        debounceTimeouts.current.delete(key);
        activeKeys.current.delete(key);
        lastValues.current.delete(key);
        onReleasedRef.current?.(key);
        memoizedFunc(key, value);
      }, delay);

      debounceTimeouts.current.set(key, timeout);
    },
    [memoizedFunc, delay]
  );

  // Flush on unmount so each pending debounced call fires with its latest value (TAS-3436: custom-field text inputs share this hook and lost typing on page switch).
  useEffect(() => {
    return () => {
      debounceTimeouts.current.forEach((timeout, key) => {
        clearTimeout(timeout);
        if (activeKeys.current.has(key)) {
          onReleasedRef.current?.(key);
        }
        if (lastValues.current.has(key)) {
          memoizedFuncRef.current(key, lastValues.current.get(key) as T);
        }
      });
      debounceTimeouts.current.clear();
      activeKeys.current.clear();
      lastValues.current.clear();
    };
  }, []);

  return debounce;
}
